'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Phone, ChevronDown, Users, Clock, Loader2 } from 'lucide-react';
import { generateCallId } from '@/lib/client-utils';
import { useAppSelector } from '@/store/hooks';
import { useAppShell } from '../AppShellContext';
import { useRunbook } from '@/lib/runbook/client';
import { humanizeIncidentType } from '@/lib/incidentDisplay';
import type { CalendarCall } from '@/store/slices/calendarSlice';
import { isCompletedCall, mergeRecentCalls, recentCallDestination } from '@/lib/calls/recentCalls';
import { useRecentCallInventory } from './useRecentCallInventory';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface RoomStatus {
  active: boolean;
  participantCount: number;
  participants: { identity: string; name: string }[];
}

const STATUS_CACHE_TTL = 30_000; // 30 seconds

function formatRelativeTime(isoDate: string | null): string {
  if (!isoDate) return '';
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function statusDot(call: CalendarCall, liveStatus?: RoomStatus): { color: string; label: string } {
  if (isCompletedCall(call)) return { color: 'bg-ink-muted', label: 'Completed' };
  if (liveStatus?.active && liveStatus.participantCount > 0) {
    return { color: 'bg-success', label: 'Live' };
  }
  if (call.status === 'active' && liveStatus && !liveStatus.active) {
    return { color: 'bg-warning', label: 'Empty' };
  }
  if (call.status === 'active') return { color: 'bg-success', label: 'Active' };
  if (call.status === 'training') return { color: 'bg-cat-4', label: 'Training' };
  if (call.status === 'completed') return { color: 'bg-ink-muted', label: 'Completed' };
  return { color: 'bg-ink-muted', label: call.status };
}

export function InstantCallButton() {
  const router = useRouter();
  const { user } = useAppShell();
  const calls = useAppSelector((state) => state.calendar.calls);
  const rbLabel = useRunbook('ui-frontend.shell.header.instant_call_label', 'Instant Call');

  const [roomStatuses, setRoomStatuses] = useState<Record<string, RoomStatus>>({});
  const [statusLoading, setStatusLoading] = useState(false);
  const lastFetchRef = useRef<number>(0);
  const lastIdsRef = useRef<string>('');
  const [menuOpen, setMenuOpen] = useState(false);
  const inventory = useRecentCallInventory(user?.id);

  // Invalidate cached room statuses when page is restored from bfcache
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        lastFetchRef.current = 0; // force re-fetch on next dropdown open
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const recentCalls = useMemo(
    () => mergeRecentCalls(inventory.rows, calls, user?.id),
    [inventory.rows, calls, user?.id],
  );

  const fetchRoomStatuses = useCallback(async () => {
    const roomNames = recentCalls.filter((c) => !isCompletedCall(c)).map((c) => c.call_id).filter(Boolean);
    if (roomNames.length === 0) return;

    const now = Date.now();
    const ids = roomNames.join('\n');
    if (ids === lastIdsRef.current && now - lastFetchRef.current < STATUS_CACHE_TTL) return;

    setStatusLoading(true);
    try {
      const res = await fetch('/api/room-status/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomNames }),
      });
      if (res.ok) {
        const data = await res.json();
        setRoomStatuses(data.rooms || {});
        lastFetchRef.current = now;
        lastIdsRef.current = ids;
      }
    } catch {
      // Silently fail — dropdown still shows DB status
    } finally {
      setStatusLoading(false);
    }
  }, [recentCalls]);

  // Room status follows the inventory: once it has settled (success or error) while the menu is open.
  const settled = inventory.phase === 'ready' || inventory.phase === 'error';
  useEffect(() => {
    if (menuOpen && settled) void fetchRoomStatuses();
  }, [menuOpen, settled, fetchRoomStatuses]);

  const handleOpenChange = useCallback((open: boolean) => {
    setMenuOpen(open);
    if (open) inventory.load();
  }, [inventory.load]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNewCall = useCallback(() => {
    router.push(`/calls/${generateCallId()}`);
  }, [router]);

  const handleOpenCall = useCallback(
    (call: CalendarCall) => {
      router.push(recentCallDestination(call));
    },
    [router],
  );

  return (
    <div className="hidden sm:flex items-stretch">
      {/* Primary: New Call */}
      <button
        onClick={handleNewCall}
        data-demo-id="header-instant-call-btn"
        className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 hover:border-blue-400/50 rounded-l-lg transition-all duration-200 group"
      >
        <div className="p-1 rounded bg-blue-500/20 group-hover:scale-105 transition-all duration-200">
          <Phone className="w-3.5 h-3.5" />
        </div>
        <span>{rbLabel}</span>
      </button>

      {/* Dropdown: Recent Calls */}
      {/* ENRICHED menu (std-overlays G14): live room status per call, on the shared menu look. */}
      <DropdownMenu look="standard" onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center px-2 text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 border border-l-0 border-blue-500/30 hover:border-blue-400/50 rounded-r-lg transition-all duration-200"
            aria-label="Recent calls"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" aria-label="Recent calls" className="w-80">
          <DropdownMenuLabel className="flex items-center justify-between">
            <span>Recent Calls</span>
            {statusLoading && <Loader2 className="w-3 h-3 animate-spin text-ink-muted" />}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {inventory.phase === 'error' && (
            <div
              role="alert"
              data-testid="recent-calls-error"
              className="px-2.5 py-2 flex items-center justify-between gap-2 text-meta text-danger"
            >
              <span>Couldn&apos;t load recent calls.</span>
              <button
                type="button"
                data-testid="recent-calls-retry"
                className="underline text-ink-secondary"
                onClick={() => inventory.load()}
              >
                Retry
              </button>
            </div>
          )}

          {recentCalls.length === 0 ? (
            inventory.phase === 'loading' ? (
              <div data-testid="recent-calls-loading" className="px-2.5 py-4 text-center text-meta text-ink-muted">
                Loading recent calls…
              </div>
            ) : inventory.phase === 'ready' ? (
              <div className="px-2.5 py-4 text-center text-meta text-ink-muted">
                No recent calls in the last 7 days
              </div>
            ) : null
          ) : (
            recentCalls.map((call) => {
              const live = isCompletedCall(call) ? undefined : roomStatuses[call.call_id];
              const dot = statusDot(call, live);
              const time = call.start_time || call.scheduled_time;

              return (
                <DropdownMenuItem
                  key={call.id}
                  className="items-start py-2"
                  onClick={() => handleOpenCall(call)}
                >
                  {/* Status dot */}
                  <div className="pt-1.5 flex-shrink-0">
                    <div className={`w-2 h-2 rounded-full ${dot.color}`} title={dot.label} />
                  </div>

                  {/* Call info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate">
                        {call.caller_name || 'Unknown Caller'}
                      </span>
                      {call.case_number && (
                        <span className="text-chip px-1.5 py-0.5 rounded-item bg-raised text-ink-secondary font-mono flex-shrink-0">
                          {call.case_number}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {call.incident_type && (
                        <span className="text-meta text-ink-secondary truncate">{humanizeIncidentType(call.incident_type)}</span>
                      )}
                      {time && (
                        <span className="text-meta text-ink-secondary flex items-center gap-1 flex-shrink-0">
                          <Clock className="w-2.5 h-2.5" />
                          {formatRelativeTime(time)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Participant count */}
                  {live?.active && live.participantCount > 0 && (
                    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-item bg-success-soft text-success text-chip flex-shrink-0">
                      <Users className="w-3 h-3" />
                      <span>{live.participantCount}</span>
                    </div>
                  )}
                </DropdownMenuItem>
              );
            })
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="justify-center"
            onClick={() => router.push('/dashboard?filter=active')}
          >
            View All Calls
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
